import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { EstadoBadge } from "@/components/Badges";
import { eur, API_BASE } from "@/lib/api";
import { FilePdf, Warning, Buildings, Trash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

function Field({ label, value, testid }) {
  return (
    <div className="border-b border-slate-100 py-2">
      <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">{label}</div>
      <div data-testid={testid} className="text-xs sm:text-sm text-slate-800 font-medium break-words">{value || "—"}</div>
    </div>
  );
}

export function FacturaDrawer({ factura, open, onOpenChange, onDelete }) {
  if (!factura) return null;
  const pdfUrl = `${API_BASE}/facturas/${factura.id}/pdf`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="factura-drawer"
        className="w-full sm:max-w-xl md:max-w-2xl overflow-y-auto p-0 z-50"
      >
        <SheetHeader className="px-4 sm:px-6 pt-5 pb-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <SheetTitle className="font-display text-lg sm:text-xl tracking-tight truncate">
                {factura.proveedor_normalizado || factura.proveedor || "Factura"}
              </SheetTitle>
              {factura.proveedor && factura.proveedor !== factura.proveedor_normalizado && (
                <div className="text-xs text-slate-400 flex items-center gap-1 mt-0.5 truncate">
                  <Buildings size={12} /> Original: {factura.proveedor}
                </div>
              )}
            </div>
            <EstadoBadge estado={factura.estado} />
          </div>
          <SheetDescription className="text-xs font-mono text-slate-500 truncate">
            {factura.nombre_archivo}
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 sm:px-6 py-5">
          {factura.motivo && (
            <div
              data-testid="factura-motivo"
              className="mb-5 flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
            >
              <Warning size={16} className="mt-0.5 shrink-0" />
              <span>{factura.motivo}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            <Field label="CIF / NIF" value={factura.cif_nif} testid="d-cif" />
            <Field label="Nº Factura" value={factura.num_factura} testid="d-num" />
            <Field label="Fecha emisión" value={factura.fecha} testid="d-fecha" />
            <Field label="Vencimiento" value={factura.fecha_vencimiento} />
            <Field label="Pestaña Sheet" value={factura.sheet_tab || "Facturas"} />
            <Field label="Origen" value={factura.origen} />
          </div>

          <div className="mt-5">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 mb-2">
              Desglose IVA (Hostelería & Régimen General)
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
                    <th className="py-1">Tipo</th>
                    <th className="py-1 text-right">Base Imponible</th>
                    <th className="py-1 text-right">Cuota IVA</th>
                  </tr>
                </thead>
                <tbody>
                  {factura.base_4 > 0 && (
                    <tr className="border-b border-slate-100">
                      <td className="py-1.5 font-medium text-emerald-700">4% (Superreducido)</td>
                      <td className="py-1.5 text-right kpi-num">{eur(factura.base_4)}</td>
                      <td className="py-1.5 text-right kpi-num">{eur(factura.iva_4)}</td>
                    </tr>
                  )}
                  {factura.base_10 > 0 && (
                    <tr className="border-b border-slate-100">
                      <td className="py-1.5 font-medium text-blue-700">10% (Alimentos/Bebidas)</td>
                      <td className="py-1.5 text-right kpi-num">{eur(factura.base_10)}</td>
                      <td className="py-1.5 text-right kpi-num">{eur(factura.iva_10)}</td>
                    </tr>
                  )}
                  {factura.base_21 > 0 && (
                    <tr className="border-b border-slate-100">
                      <td className="py-1.5 font-medium text-purple-700">21% (General)</td>
                      <td className="py-1.5 text-right kpi-num">{eur(factura.base_21)}</td>
                      <td className="py-1.5 text-right kpi-num">{eur(factura.iva_21)}</td>
                    </tr>
                  )}
                  {factura.base_otro > 0 && (
                    <tr className="border-b border-slate-100">
                      <td className="py-1.5 font-medium text-slate-700">Otros / Exento</td>
                      <td className="py-1.5 text-right kpi-num">{eur(factura.base_otro)}</td>
                      <td className="py-1.5 text-right kpi-num">{eur(factura.iva_otro)}</td>
                    </tr>
                  )}
                  {(!factura.base_4 && !factura.base_10 && !factura.base_21 && !factura.base_otro) && (
                    (factura.lineas_iva || []).map((l, i) => (
                      <tr key={i} className="border-b border-slate-100">
                        <td className="py-1.5">{l.tipo}%</td>
                        <td className="py-1.5 text-right kpi-num">{eur(l.base)}</td>
                        <td className="py-1.5 text-right kpi-num">{eur(l.cuota)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-5 rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-1.5">
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-slate-500">Base imponible total</span>
              <span className="kpi-num font-medium">{eur(factura.base_total)}</span>
            </div>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="text-slate-500">Total IVA soportado</span>
              <span className="kpi-num font-medium">{eur(factura.iva_total)}</span>
            </div>
            <div className="flex justify-between text-sm sm:text-base font-bold border-t border-slate-200 pt-1.5">
              <span>TOTAL FACTURA</span>
              <span data-testid="d-total" className="kpi-num text-slate-900">{eur(factura.total)}</span>
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400 mb-2">
              <FilePdf size={14} /> Documento archivado (Drive)
            </div>
            <iframe
              data-testid="factura-pdf-preview"
              title="pdf"
              src={pdfUrl}
              className="w-full h-[280px] sm:h-[380px] rounded-lg border border-slate-200 bg-white"
            />
            <div className="mt-1 font-mono text-[10px] sm:text-[11px] text-slate-400 break-all">
              {factura.drive_path}/{factura.nombre_archivo}
            </div>
          </div>

          {onDelete && (
            <div className="mt-6 pt-4 border-t border-slate-200 flex flex-col sm:flex-row justify-between sm:items-center gap-3 bg-slate-50/70 -mx-4 sm:-mx-6 -mb-5 p-4 rounded-b-lg">
              <span className="text-xs text-slate-500">¿Esta factura se incorporó por error?</span>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onDelete(factura.id)}
                className="gap-1.5 bg-rose-600 hover:bg-rose-700 text-white shadow-sm self-start sm:self-auto"
              >
                <Trash size={15} />
                Eliminar factura
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

